/**
 * 在庫消費モーダル
 *
 * `InventoryPage` から切り出した。フォームをモーダル表示中だけマウントし、
 * 非表示中に Form インスタンスへアクセスしないようにする狙いもある。
 */
import { Button, Form, Input, InputNumber, Modal, Select, Space, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useIsFormSubmittable } from '@/shared/hooks/useIsFormSubmittable';
import {
  CONSUMPTION_REASON_OPTIONS,
  type ConsumptionReasonCode,
} from '../types/inventory';

const { TextArea } = Input;

/** 消費数量の最小値(1以上の整数) */
const CONSUMPTION_QUANTITY_MIN = 1;
const NOTE_TEXTAREA_ROWS = 3;

/** 消費フォームの入力値 */
export interface InventoryConsumptionFormValues {
  readonly quantity: number;
  readonly reasonCode: ConsumptionReasonCode;
  readonly note?: string;
}

interface Props {
  readonly open: boolean;
  readonly title: string;
  readonly isSubmitting: boolean;
  readonly onSubmit: (values: InventoryConsumptionFormValues) => Promise<void>;
  readonly onCancel: () => void;
}

export function InventoryConsumptionModal({
  open,
  title,
  isSubmitting,
  onSubmit,
  onCancel,
}: Props): React.ReactElement {
  const [form] = Form.useForm<InventoryConsumptionFormValues>();
  const isSubmittable = useIsFormSubmittable({ form, isFormVisible: open });

  const handleFinish = async (values: InventoryConsumptionFormValues): Promise<void> => {
    await onSubmit(values);
  };

  return (
    <Modal title={title} open={open} onCancel={onCancel} footer={null} destroyOnHidden>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="quantity"
          label="数量"
          rules={[
            { required: true, message: '数量を入力してください' },
            {
              type: 'integer',
              min: CONSUMPTION_QUANTITY_MIN,
              message: '1以上の整数を入力してください',
            },
          ]}
        >
          <InputNumber className="w-full" placeholder="消費数量を入力" />
        </Form.Item>
        <Form.Item
          name="reasonCode"
          label="理由"
          rules={[{ required: true, message: '理由を選択してください' }]}
        >
          <Select placeholder="消費理由を選択">
            {CONSUMPTION_REASON_OPTIONS.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                <Space size={4}>
                  {option.label}
                  <Tooltip title={option.tooltip}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="note" label="特記事項">
          <TextArea rows={NOTE_TEXTAREA_ROWS} placeholder="特記事項があれば入力" />
        </Form.Item>
        <Form.Item className="mb-0 text-right">
          <Space>
            <Button onClick={onCancel}>キャンセル</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={!isSubmittable}
            >
              消費する
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}
